import { useSelector } from "react-redux";
import type { ApplicationState } from "@ppb/tbd-store/state/ApplicationState.types";
import { createFindCardbyURNSelector } from "@ppb/tbd-store/state/layout/cards/cards-selectors";

const getFindCardByURN = createFindCardbyURNSelector();

/**
 * Whether a card urn is an exchange market, read off the normalized card's `displayRunners` (the
 * normalizer only writes the `exchange` key when the BFF sent an exchange market). Used to gate the
 * migrated MarketCard per product, since exchange rolls out behind its own throttle.
 *
 * Read through its own hook, like the throttles, so PebbleContent's two render paths can be tested by
 * mocking one hook rather than driving a whole layout slice through `useSelector`.
 *
 * NOTE: this depends on MarketCard still being catalogue-fetched, i.e. deliberately absent from
 * `APOLLO_MIGRATED_CARDS`. If it ever joins that list the Redux entity stops existing, this silently
 * reads false, and exchange flips onto the migrated card with no throttle change — the two have to move
 * together.
 */
export const useIsExchangeMarketCard = (urn: string): boolean =>
  useSelector((state: ApplicationState) => {
    const card = getFindCardByURN(state.layouts.cards, urn);

    return !!(card && "displayRunners" in card && card.displayRunners?.exchange);
  });
